import { useEffect, useRef } from "react";

/** A global key binding. `key` is matched against `KeyboardEvent.key`. */
export type Shortcut = {
  key: string;
  handler: (e: KeyboardEvent) => void;
  /** Fire even while a text field / editor is focused (default: false). */
  allowInEditable?: boolean;
};

/** True when the event target is something the user is typing into — a text
 * input, textarea, select, or contenteditable region. Global shortcuts skip
 * these so they never clobber editing (markdown editor, search box, …). */
export function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable
  );
}

/**
 * Register global keyboard shortcuts via a single document-level listener.
 *
 * Modified chords (meta/ctrl/alt) are ignored so OS/browser shortcuts pass
 * through; Shift is allowed (e.g. "?" is Shift+"/"). Bindings are read through
 * a ref so the listener is attached once and never re-subscribes on render.
 */
export function useShortcuts(shortcuts: Shortcut[]): void {
  const ref = useRef(shortcuts);
  ref.current = shortcuts;
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      for (const s of ref.current) {
        if (e.key !== s.key) continue;
        if (!s.allowInEditable && isEditable(e.target)) return;
        e.preventDefault();
        s.handler(e);
        return;
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
}
